import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  ahuBoardApi,
  useGetAccessoryDetailQuery,
  useGetSectionAccessoriesQuery,
} from 'app/services/ahuBoardApi';
import {
  ahuBoardFormsApi,
  useDeleteAhuAccessoryMutation,
} from 'app/services/ahuBoardFormsApi';
import {
  setExpandedAccessory,
  setSelectedAccessory,
  openDuplicateAccessoryPopup,
  openDeleteAccessoryPopup,
} from 'app/slices/ahuBoardSlice';
import type { IAhuAccessoryRow, IAhuBoardSection, IAhuBoardTunnel, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  validateSectionAccessories,
} from '../../validation/validationController';
import AccessoryFormHost, {
  isAccessoryFormBuilt,
} from '../../forms/accessory/AccessoryFormHost';
import { suppressOpenGroupOnce } from '../../forms/accessory/useAccessoryForm';

/**
 * Phase 6e1 — accessories tab HOSTING shell (§7.6, tab.accessories.js +
 * tabs.accessory.common.tmpl, markup faithful): per-section containers, the
 * common row chrome (trash / duplicate anchor / activity radio-checkbox /
 * label + tag span + expand icon / per-row Delete Accessory popup / form
 * container preloader / per-row mounting-popup host), UVC name-only rows,
 * "Section has no Accessories" placeholder.
 *
 * Activity consumes the 6d slice seams (accessoriesTab.expandedAccessoryId —
 * §13 rows 8/11): radio-like check fires `select:accessory`,
 * `select:deselect:accessory`, arms `#accessories-ul` and auto-opens the
 * row's first subtab (slice half); collapsed-row hover → `hover:accessory`.
 *
 * Delete runs the §7.6 removeAccessory cascade (ESP/type extras + fan-ESP
 * refetch + lug recalc). Row FORM bodies = 6e2.
 */

const ESP_TYPES = ['opening', 'louver', 'damper', 'hood', 'user_defined_accessory'];

const ACCESSORY_PLURALS: Record<string, string> = {
  damper: 'dampers',
  louver: 'louvers',
  access_door: 'access_doors',
  opening: 'openings',
  hood: 'hoods',
  drain_pan: 'drain_pans',
  floor_drain: 'floor_drains',
  light: 'lights',
  vfd: 'vfds',
  jbox: 'jboxes',
  disconnect: 'disconnects',
  enclosure: 'enclosures',
  starter: 'starters',
  adp_gage: 'adp_gages',
  uvc: 'uvcs',
  dog_house: 'dog_houses',
  user_defined_accessory: 'user_defined_accessories',
};

interface TabAccessoriesProps {
  ahuId: number;
  sections: IAhuBoardSection[];
  /** 6e2b — accessory forms need the tunnel (aw dims) + board cms doc. */
  tunnels?: IAhuBoardTunnel[];
  cms?: ICmsAhuDoc;
}

// Legacy row labels use the serializer's `name` field — the AHUAccessory
// model's display-name property ('VFD', 'Access door', 'Drain pan', …;
// models_accessory.py:320) — NOT the raw type ('vfd'/'access_door').
const displayName = (row: IAhuAccessoryRow): string => {
  const name = String(row.name ?? row.label ?? row.type ?? '');
  return name;
};

/**
 * Dev-sync B4 — the row's trash icon, HIDDEN for a VFD carrying fan_motor_id
 * (dev accessories/vfd/view/form.js:135-140 _toggleDeleteIcon): such VFDs are
 * created from a fan's Has-VFD checkbox and are removed by unchecking it on
 * the fan, not via the accessory list. The generic row list (AHUAccessory-
 * Serializer: id/type/name/section_id/tag) has no fan_motor_id, so VFD rows
 * read the concrete detail — normally a boot-warmed RTKQ cache hit (the
 * boot-draw feed fetches every drawable accessory's detail), and the tag is
 * invalidated by the vfd PATCH path so a later fan-link shows through.
 * Legacy runs the toggle from the form's postRenderCallback (every form
 * renders at boot); React mounts forms on first expand, so the gate lives on
 * the row instead — same net behavior from first paint.
 */
const AccessoryRemoveIcon = ({
  row,
  onDelete,
}: {
  row: IAhuAccessoryRow;
  onDelete: () => void;
}) => {
  const isVfd = String(row.type ?? '') === 'vfd';
  const { data: detail } = useGetAccessoryDetailQuery(
    { plural: 'vfds', id: row.id },
    { skip: !isVfd }
  );
  const hidden = isVfd && Boolean(detail?.fan_motor_id);
  return (
    <a
      data-id={row.id}
      className={`accessory_remove icon-trash${hidden ? ' hide' : ''}`}
      {...{ tooltip: 'Delete Accessory' }}
      onClick={onDelete}
    />
  );
};

/**
 * has-vfd-fix — vfd/view/form.js's _toggleCardVisibility. Unchecking "Has
 * VFD" on the fan doesn't delete this accessory row (AHUFan.reset_vfd just
 * clears master_vfd/vfd_params so BOM/pricing history and the fan_motor
 * link survive a re-check), so without this the accessories list keeps
 * showing a full VFD card with stale catalog data. check_vfd mirrors the
 * same has_vfd gate the canvas (buildAccessoryBootDraw) and FreeCAD
 * generator (ahu.xml) already use -- hide the whole card here too, not just
 * the delete icon (AccessoryRemoveIcon) or leave it visible with disabled
 * fields. A separate no-render component (rather than computing this
 * inline in the row map below) because useGetAccessoryDetailQuery can't be
 * called a variable number of times per render inside .map.
 */
const VfdCardVisibility = ({ row }: { row: IAhuAccessoryRow }) => {
  const isVfd = String(row.type ?? '') === 'vfd';
  const { data: detail } = useGetAccessoryDetailQuery(
    { plural: 'vfds', id: row.id },
    { skip: !isVfd }
  );
  useEffect(() => {
    if (!isVfd) return;
    const li = document.getElementById(`forms_accessory_${row.id}`);
    li?.classList.toggle('hide', !detail?.check_vfd);
  }, [isVfd, row.id, detail?.check_vfd]);
  return null;
};

const SectionAccessories = ({
  section,
  ahuId,
  tunnel,
  cms,
}: {
  section: IAhuBoardSection;
  ahuId: number;
  tunnel?: IAhuBoardTunnel | null;
  cms?: ICmsAhuDoc;
}) => {
  const dispatch = useAppDispatch();
  const { data: rows, refetch } = useGetSectionAccessoriesQuery(section.id);
  const currentTab = useAppSelector((s) => s.ahuBoard.selection.currentTab);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const expandedId = useAppSelector((s) => s.ahuBoard.accessoriesTab.expandedAccessoryId);
  const deleteSeamId = useAppSelector((s) => s.ahuBoard.popupSeams.deleteAccessoryId);
  const [deleteRowId, setDeleteRowId] = useState<number | null>(null);
  const [deleteAccessory] = useDeleteAhuAccessoryMutation();
  // QA batch 4 #5 — forms mount EAGERLY like legacy (tab.accessories.js
  // render_accessories builds every concrete accessory view at boot). The
  // 6e1-3 mount-on-first-expand deferral hid the `.form-heading` Smartlist
  // chip (counts/Reset/SELECT — absolutely positioned onto the row header,
  // visible in the COLLAPSED state) until a row had been opened once.
  // 6e2b — §49.6 changeTagOnBoard receiver: live row-label tag echo
  // (UDA "UD : <name>" + tag edits — parity 339) via RTKQ cache patch.
  useEffect(() => {
    const off = ahuBoardBus.on('update:accessories:tag:onboard', ({ tag, id }) => {
      dispatch(
        ahuBoardApi.util.updateQueryData('getSectionAccessories', section.id, (list) => {
          const target = list.find((r) => r.id === id);
          if (target) target.tag = tag;
        })
      );
    });
    return off;
  }, [dispatch, section.id]);

  const isActive = currentTab === 'accessories' && currentSectionId === section.id;

  // §13 row 12 — context-menu Delete writes popupSeams.deleteAccessoryId;
  // the owning row's popup opens.
  useEffect(() => {
    if (deleteSeamId != null && (rows ?? []).some((r) => r.id === deleteSeamId)) {
      setDeleteRowId(deleteSeamId);
      dispatch(openDeleteAccessoryPopup(null));
    }
  }, [deleteSeamId, rows, dispatch]);

  // `added:new:accessory` → programmatic row select (§7.6 / §13 row 11).
  // Legacy keeps `lastAdded`, clicks `accessory_activity_<id>` when the row
  // is already in the DOM and otherwise renders the arriving row checked
  // (tab.accessories.js:157-165, :246). The old 200 ms timer here read a
  // STALE `rows` closure (the emit precedes the refetch re-render) and never
  // expanded — QA batch 8 #3c. Hold the pending id instead and expand from
  // the CURRENT list. Child subtabs stay collapsed like legacy
  // (accessory_view.js:313-314) — the add-path expand marks
  // suppressOpenGroupOnce so the form's openGroup-parity effect skips its
  // manual-expand open-all exactly once.
  const pendingAddedIdRef = useRef<number | null>(null);
  useEffect(() => {
    const off = ahuBoardBus.on('added:new:accessory', (accessoryId) => {
      if ((rows ?? []).some((r) => r.id === accessoryId)) {
        // Add-path expand keeps child subtabs collapsed (legacy
        // render-checked branch — see suppressOpenGroupOnce).
        suppressOpenGroupOnce.accessoryId = accessoryId;
        selectRow(accessoryId, true);
      } else {
        pendingAddedIdRef.current = accessoryId;
        void refetch();
      }
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Pending add — expand once the refetched list contains the new row.
  useEffect(() => {
    const id = pendingAddedIdRef.current;
    if (id != null && (rows ?? []).some((r) => r.id === id)) {
      pendingAddedIdRef.current = null;
      // Add-path expand keeps child subtabs collapsed (legacy
      // render-checked branch — see suppressOpenGroupOnce).
      suppressOpenGroupOnce.accessoryId = id;
      selectRow(id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // The psychrometric chart's SYNCHRONIZE CONDITIONS jump naming one of this
  // section's accessories. Routed through `selectRow` rather than through the
  // two slice dispatches on their own, so the jump gets the whole select
  // sequence the row click gets — the canvas highlight and the toolbar's
  // accessory name both ride on the bus events, not on the slice (review
  // round 2 follow-up). `useAccessoryForm` opens Performance and clears the
  // jump; this effect only has to run in the same commit, which it does
  // because a child's effect clearing the store cannot change the value this
  // one already rendered with.
  const performanceJump = useAppSelector((s) => s.ahuBoard.performanceJump);
  useEffect(() => {
    const id = performanceJump?.accessoryId;
    if (id == null || !(rows ?? []).some((r) => r.id === id)) return;
    if (expandedId !== id) selectRow(id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceJump, rows]);

  // QA batch 8 #3b — legacy pre_render_accessory removes the insert-strip
  // loading placeholder the moment the real accessory row renders
  // (tab.accessories.js:230-239); the strip's 5 s timeout is only a failure
  // backstop.
  useEffect(() => {
    (rows ?? []).forEach((row) => {
      document
        .querySelector(
          `#forms_accessories_${section.id} .accessory-loading-${String(row.type ?? '')}`
        )
        ?.remove();
    });
  }, [rows, section.id]);

  const writeToolbarInfo = (row: IAhuAccessoryRow | null) => {
    document
      .querySelectorAll<HTMLElement>('#canvas_toolbar #accessories-ul span.accessory_info')
      .forEach((el) => {
        el.textContent = row
          ? ` : ${displayName(row)}${row.tag ? ` : ${row.tag}` : ''}`
          : '';
      });
  };

  const selectRow = (id: number, checked: boolean) => {
    const row = (rows ?? []).find((r) => r.id === id) ?? null;
    if (checked) {
      dispatch(setExpandedAccessory(id));
      dispatch(setSelectedAccessory(id));
      ahuBoardBus.emit('select:accessory', id);
      writeToolbarInfo(row);
      ahuBoardBus.emit('select:deselect:accessory', id, true);
    } else {
      dispatch(setExpandedAccessory(null));
      dispatch(setSelectedAccessory(null));
      ahuBoardBus.emit('select:accessory', null);
      writeToolbarInfo(null);
      ahuBoardBus.emit('select:deselect:accessory', id, false);
    }
  };

  const removeAccessory = (row: IAhuAccessoryRow) => {
    const type = String(row.type ?? '');
    deleteAccessory(row.id)
      .unwrap()
      .then(() => {
        // §7.6 removeAccessory cascade.
        ahuBoardBus.emit('remove_accessory_from_canvas', row.id);
        // …and the record itself is gone, which the canvas channel above does
        // NOT imply (the draw gate hides live rows through it too).
        ahuBoardBus.emit('accessory:deleted', row.id);
        if (expandedId === row.id) {
          dispatch(setExpandedAccessory(null));
          dispatch(setSelectedAccessory(null));
          ahuBoardBus.emit('select:accessory', null);
          writeToolbarInfo(null);
        }
        void dispatch(validateSectionAccessories(section.id));
        if (type === 'damper') {
          ahuBoardBus.emit(`remove_damper_accessory_${section.id}`);
        }
        if (type === 'uvc') ahuBoardBus.emit('rating_mode:hide');
        if (ESP_TYPES.includes(type)) {
          ahuBoardBus.emit(`remove_accessory_esp_${row.id}`);
          ahuBoardBus.emit(`refetch_fan_${section.id}_isp`);
        }
        if (type === 'hood') ahuBoardBus.emit('hood:removed:from:section');
        // A damper mounted on this louver has just been handed back to the
        // wall the louver was on; its form is still showing it mounted.
        if (type === 'louver')
          ahuBoardBus.emit('louver:damper:recheck', row.id, Number(section.id));
        // Fan-ESP refetch across the AHU (writes `.fan_<id>` inputs +
        // `force:render:fan:<id>` — cross-form DOM write kept, §7.6).
        const componentsInAhu = dispatch(
          ahuBoardFormsApi.endpoints.getComponentsInAhu.initiate(ahuId, {
            forceRefetch: true,
          })
        );
        componentsInAhu
          .unwrap()
          .then((components) => {
            components
              .filter((c) => String(c.type ?? '') === 'fan')
              .forEach((fanRow) => {
                const fanId = Number(fanRow.id);
                dispatch(
                  ahuBoardFormsApi.endpoints.getFanEsp.initiate(fanId, {
                    forceRefetch: true,
                  })
                )
                  .unwrap()
                  .then((esp) => {
                    document
                      .querySelectorAll<HTMLInputElement>(`.fan_${fanId}`)
                      .forEach((input) => {
                        input.value = String(
                          (esp as Record<string, unknown>).esp ?? ''
                        );
                      });
                    ahuBoardBus.emit(`force:render:fan:${fanId}`);
                  })
                  .catch(() => undefined);
              });
          })
          .catch(() => undefined);
        ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
          tunnel_id: section.tunnel_id,
          hide_from_canvas: true,
        });
        dispatch(
          ahuBoardApi.util.updateQueryData('getSectionAccessories', section.id, (list) =>
            list.filter((r) => r.id !== row.id)
          )
        );
      })
      .catch(() => undefined)
      .finally(() => setDeleteRowId(null));
  };

  const list = rows ?? [];

  return (
    <div
      id={`forms_accessories_${section.id}`}
      className={`accessories${isActive ? ' active' : ''}`}
    >
      <ul className="inputs-group">
        {!list.length && <li className="empty-accessory">Section has no Accessories</li>}
        {list.map((row) => {
          const type = String(row.type ?? '');
          const name = displayName(row);
          const isUvc = name === 'UVC' || type === 'uvc';
          const isUda = name === 'User Defined' || type === 'user_defined_accessory';
          const expanded = expandedId === row.id;
          return (
            <li
              key={row.id}
              id={`forms_accessory_${row.id}`}
              className={`accessory accessory_${type}`}
              data-id={row.id}
              onMouseEnter={() => {
                if (!expanded) ahuBoardBus.emit('hover:accessory', row.id, true);
              }}
              onMouseLeave={() => {
                if (!expanded) ahuBoardBus.emit('hover:accessory', row.id, false);
              }}
            >
              <AccessoryRemoveIcon row={row} onDelete={() => setDeleteRowId(row.id)} />
              <VfdCardVisibility row={row} />
              <a
                data-id={row.id}
                className="accessory_duplicate"
                onClick={() => dispatch(openDuplicateAccessoryPopup(row.id))}
              />
              {!isUvc ? (
                <>
                  <input
                    id={`accessory_activity_${row.id}`}
                    type="checkbox"
                    data-id={row.id}
                    data-info={` : ${name} ${row.tag ? `: ${row.tag}` : ''}`}
                    name="accessory_activity"
                    checked={expanded}
                    onChange={(e) => selectRow(row.id, e.currentTarget.checked)}
                  />
                  {!isUda ? (
                    <label htmlFor={`accessory_activity_${row.id}`} className={name}>
                      {name === 'ADP Gage' ? (
                        'APD Gage '
                      ) : (
                        <>
                          {' '}
                          {name}{' '}
                          <span
                            id={`accessories_tag_${row.id}`}
                            className="accessories_tag"
                          >
                            {row.tag ? `: ${row.tag}` : ''}
                          </span>
                        </>
                      )}
                      <i className="icon-add accessory-expand" {...{ tooltip: 'Expand' }} />
                    </label>
                  ) : (
                    <label htmlFor={`accessory_activity_${row.id}`} className={name}>
                      UD
                      <span id={`accessories_tag_${row.id}`} className="accessories_tag">
                        {row.tag ? `: ${row.tag}` : ''}
                      </span>
                      <i className="icon-add accessory-expand" {...{ tooltip: 'Expand' }} />
                    </label>
                  )}
                  {row.tag ? (
                    <span className="tag-tooltip hide" data-id={row.id}>
                      {row.tag}
                    </span>
                  ) : null}
                </>
              ) : (
                <div className={`accessory_activity_${row.id} uvc_accessory_activity`}>
                  <span className="uvc-name">{name}</span>
                </div>
              )}

              {/* Per-row Delete Accessory popup (tabs.accessory.common.tmpl). */}
              <div
                className={`popup-wrapper delete-accessory-popup width-small${
                  deleteRowId === row.id ? ' active' : ''
                }`}
              >
                <div className="popup-content">
                  <h2 className="popup-heading">Delete Accessory?</h2>
                  <div>
                    <div className="clearfix popup-content-part">
                      <p>
                        If you choose to Delete, all Accessory Data will be lost and will
                        not be recoverable.
                      </p>
                      <p>
                        Section where Accessory is located:{' '}
                        {/* legacy showDeletePopup: section TAG, falling back
                            to name — never 'name : tag' (tab.accessories.js:433). */}
                        <span className="section-name-or-tag">
                          {String(section.tag || section.name || '')}
                        </span>
                      </p>
                      <span className="section-tag-value">
                        {name}
                        {row.tag ? ` : ${row.tag}` : ''}
                      </span>
                    </div>
                    <div className="popup-control-buttons">
                      <button
                        data-id={row.id}
                        className="delete not-highlighted-btn"
                        name="delete_accessory_btn"
                        onClick={() => removeAccessory(row)}
                      >
                        DELETE
                      </button>
                      <button
                        className="default-btn highlighted-btn"
                        name="cancel_delete_accessory_btn"
                        onClick={() => setDeleteRowId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6e2b — concrete accessory form (all 17+ams types), mounted
                  eagerly (QA batch 4 #5 — legacy renders every form at boot;
                  the loading preloader clears once the form paints, so the
                  6e2b verifier's permanent-preloader trap cannot recur). */}
              <div
                id={`forms_${type}_${row.id}`}
                className={`accessory-wrapper accessory-wrapper-preloader accessory-wrapper_${type}`}
              >
                {isAccessoryFormBuilt(type) ? (
                  <AccessoryFormHost
                    row={row}
                    section={section}
                    tunnel={tunnel}
                    cms={cms}
                    expanded={expanded}
                  />
                ) : expanded ? (
                  // unported type — legacy-style loading state while open.
                  <div className="preloader dotted-preloader active" />
                ) : null}
              </div>

              {/* Per-row mounting-popup host (§51.4 — 6e2 fills; chrome 6e1). */}
              <div
                className={`insert_accessory_popup insert_accessory_popup_${row.id}`}
                style={{ border: 'none' }}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const TabAccessories = ({ ahuId, sections, tunnels, cms }: TabAccessoriesProps) => (
  <>
    {sections.map((section) => (
      <SectionAccessories
        key={section.id}
        section={section}
        ahuId={ahuId}
        tunnel={(tunnels ?? []).find((t) => t.id === section.tunnel_id) ?? null}
        cms={cms}
      />
    ))}
  </>
);

export default TabAccessories;

export { ACCESSORY_PLURALS };
